import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { Autocomplete as PiazzaAutocomplete } from 'ComponentLibrary/src/piazza/forms'
import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Autocomplete, AutocompleteRenderInputParams, Stack, TextField } from '@mui/material'
import { Colors, SERVICES_NEEDING_CONSENT, Typography, useCookieConsent } from 'ComponentLibrary'
import { HTMLAttributes, Key, SyntheticEvent, useContext, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Search } from 'react-feather'
import { PlaceDetails } from './types'
import { useGoogleSearchBar } from 'ComponentLibrary/src/molecules/AddressSearch/useGoogleSearchBar'
import { DEFAULT_GOOGLE_LANGUAGE } from 'ComponentLibrary/src/molecules/AddressSearch/useGoogleAPI'

type GoogleSearchProps = {
    apiKey: string
    language?: string
    addressString?: string
    setPlaceDetailsCallback: (placeDetails: PlaceDetails | null) => void
    autocompleteRate?: number
    setParentIsPending?: (isPending: boolean) => void
    onError?: (error: Error) => void
    copy?: Partial<GoogleSearchBarCopy>
}

const DEBOUNCE_IN_MS = 300

export type GoogleSearchBarCopy = {
    noMatchingAddress: string
    enterAddressToShowOptions: string
    consentRequiredLabel: string
    searchLabel: string
}

const DEFAULT_GOOGLE_SEARCH_BAR_COPY: GoogleSearchBarCopy = {
    noMatchingAddress: 'Keine passende Adresse gefunden',
    enterAddressToShowOptions: 'Adresse angeben um Optionen anzuzeigen',
    consentRequiredLabel: 'Zum Benutzen der Adresssuche müssen Sie Google Maps zulassen',
    searchLabel: 'Adresse suchen',
}

export const GoogleSearchBar = ({
    apiKey,
    language = DEFAULT_GOOGLE_LANGUAGE,
    addressString = '',
    setPlaceDetailsCallback,
    setParentIsPending,
    onError,
    copy,
}: GoogleSearchProps) => {
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const resolvedCopy = { ...DEFAULT_GOOGLE_SEARCH_BAR_COPY, ...copy }
    const { isEnabled } = useCookieConsent(SERVICES_NEEDING_CONSENT.GOOGLE_MAPS)
    const [displayedInputValue, setDisplayedInputValue] = useState(addressString)
    const [chosenValue, setChosenValue] = useState<google.maps.places.AutocompletePrediction | null>(null)
    const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([])
    const { fetchPlaceSuggestionsAsync, fetchPlaceDetails, placeDetails, isPending, hasPlaceDetailsServiceLoaded } = useGoogleSearchBar({
        apiKey,
        language,
        onError,
    })

    const debounced = useDebouncedCallback(async (value) => {
        const results = value ? (await fetchPlaceSuggestionsAsync({ input: value })).predictions : []
        setSuggestions(results)
    }, DEBOUNCE_IN_MS)

    useEffect(() => {
        setDisplayedInputValue(addressString)
        if (hasPlaceDetailsServiceLoaded) {
            fetchPlaceDetails({ address: addressString })
        }
    }, [addressString, hasPlaceDetailsServiceLoaded, fetchPlaceDetails])

    useEffect(() => {
        if (chosenValue) {
            fetchPlaceDetails({ address: chosenValue.description })
        }
    }, [chosenValue, fetchPlaceDetails])

    useEffect(() => {
        if (placeDetails) {
            setPlaceDetailsCallback(placeDetails)
        }
    }, [placeDetails, setPlaceDetailsCallback])

    useEffect(() => {
        if (setParentIsPending) {
            setParentIsPending(isPending)
        }
    }, [isPending, setParentIsPending])

    function onInputChangeHandler(event: SyntheticEvent | null, newInputValue: string) {
        if (event) {
            void debounced(newInputValue)
            setChosenValue(null)
            setDisplayedInputValue(newInputValue)
        }
    }

    function onChangeHandler(_event: SyntheticEvent, value: google.maps.places.AutocompletePrediction | null) {
        if (value) {
            setChosenValue(value)
        }
    }

    if (usePiazzaInputs) {
        return (
            <PiazzaAutocomplete
                autoComplete
                label={isEnabled ? resolvedCopy.searchLabel : resolvedCopy.consentRequiredLabel}
                disabled={!isEnabled}
                options={suggestions.map((suggestion) => ({ value: suggestion, label: suggestion.description }))}
                value={chosenValue ?? undefined}
                isValueEqual={(option, value) => option.description === value.description}
                inputValue={displayedInputValue}
                onInputChange={onInputChangeHandler}
                onValueChange={(value) => {
                    if (value) {
                        setChosenValue(value)
                    }
                }}
                filterOptions={(options) => options}
                noOptionsText={displayedInputValue ? resolvedCopy.noMatchingAddress : resolvedCopy.enterAddressToShowOptions}
                openOnFocus={false}
                popupIcon={<MagnifyingGlassIcon />}
                sx={{ '& .MuiAutocomplete-popupIndicator': { transform: 'none' } }}
                renderOption={(props, option) => renderOption(props, option.value)}
            />
        )
    }

    return (
        <Autocomplete
            autoComplete
            disabled={!isEnabled}
            filterOptions={(x) => x} // prevent autocomplete from filtering results, this is done by google suggestions
            value={chosenValue}
            noOptionsText={
                displayedInputValue ? (
                    <Typography>{resolvedCopy.noMatchingAddress}</Typography>
                ) : (
                    <Typography>{resolvedCopy.enterAddressToShowOptions}</Typography>
                )
            }
            openOnFocus={false}
            inputValue={displayedInputValue}
            onInputChange={onInputChangeHandler}
            options={suggestions}
            onChange={onChangeHandler}
            renderOption={renderOption}
            getOptionLabel={(option) => option.description}
            isOptionEqualToValue={(option, value) => option.description === value.description}
            renderInput={renderInput}
            popupIcon={<Search />}
            sx={{
                '& .MuiFormLabel-root': { maxWidth: 'calc(100% - 50px)', textOverflow: 'ellipsis', overflow: 'hidden' },
                '& .MuiAutocomplete-popupIndicator': { transform: 'none' },
                '& .MuiFormHelperText-root': { fontSize: '9px', marginTop: '1px' },
            }}
        />
    )

    function renderInput(params: AutocompleteRenderInputParams) {
        return (
            <TextField
                {...params}
                label={!isEnabled ? resolvedCopy.consentRequiredLabel : resolvedCopy.searchLabel}
                sx={{ '& .MuiInputBase-root, input': { cursor: !isEnabled ? 'not-allowed!important' : 'initial' } }}
            />
        )
    }

    function renderOption(
        { key, ...props }: HTMLAttributes<HTMLLIElement> & { key?: Key },
        option: google.maps.places.AutocompletePrediction
    ) {
        return (
            <li key={key} {...props} role="option">
                <Stack direction="row">
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                    <Typography style={{ marginLeft: '23px', marginRight: '1ch' }}>{option.structured_formatting.main_text}</Typography>
                    <Typography color={Colors.customtext.black60}>{option.structured_formatting.secondary_text}</Typography>
                </Stack>
            </li>
        )
    }
}
